import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Save } from "lucide-react";

import { type Tenant, adminUsersApi } from "@/api/adminUsers";
import { useMe } from "@/hooks/useMe";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function AdminTenantsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Tenant | null>(null);

  const isSuperAdmin = me.data?.role === "super_admin";

  const tenants = useQuery({
    queryKey: ["admin-tenants"],
    queryFn: () => adminUsersApi.listTenants(),
    enabled: isSuperAdmin,
  });

  if (!isSuperAdmin) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500">{t("common.read_only")}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("admin_tenants.title")}</CardTitle>
              <CardDescription>{t("admin_tenants.subtitle")}</CardDescription>
            </div>
            <Button onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" /> {t("admin_tenants.add")}
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {tenants.isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : (tenants.data ?? []).length === 0 ? (
            <div className="p-6 text-sm text-slate-500">{t("admin_tenants.no_tenants")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="p-2">{t("admin_tenants.name")}</th>
                    <th className="p-2">{t("admin_tenants.description")}</th>
                    <th className="p-2">{t("admin_tenants.timezone")}</th>
                    <th className="p-2">{t("common.active")}</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.data!.map((tn) => (
                    <tr
                      key={tn.id}
                      onClick={() => setEditing(tn)}
                      className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                    >
                      <td className="p-2 font-medium">{tn.name}</td>
                      <td className="p-2 text-slate-500">{tn.description ?? "—"}</td>
                      <td className="p-2 font-mono text-xs">{tn.timezone}</td>
                      <td className="p-2">{tn.is_active ? "✓" : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {(adding || editing) && (
        <TenantFormModal
          tenant={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => {
            setAdding(false);
            setEditing(null);
            void qc.invalidateQueries({ queryKey: ["admin-tenants"] });
          }}
        />
      )}
    </div>
  );
}

function TenantFormModal({
  tenant,
  onClose,
  onSaved,
}: {
  tenant: Tenant | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: tenant?.name ?? "",
    description: tenant?.description ?? "",
    timezone: tenant?.timezone ?? "Europe/Kyiv",
    is_active: tenant?.is_active ?? true,
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        description: form.description || null,
        timezone: form.timezone,
        is_active: form.is_active,
      };
      return tenant
        ? adminUsersApi.updateTenant(tenant.id, body)
        : adminUsersApi.createTenant(body);
    },
    onSuccess: () => onSaved(),
  });

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>{tenant ? t("admin_tenants.edit") : t("admin_tenants.add")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{t("admin_tenants.name")}</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>{t("admin_tenants.description")}</Label>
            <Input
              value={form.description ?? ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label>{t("admin_tenants.timezone")}</Label>
            <Input
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            {t("common.active")}
          </label>
        </CardContent>
        <div className="flex gap-2 px-6 pb-6">
          <Button onClick={() => save.mutate()} disabled={save.isPending || !form.name}>
            <Save className="h-4 w-4" />
            {save.isPending ? t("common.saving") : t("common.save")}
          </Button>
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </Card>
    </div>
  );
}
